import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { CHART_COLORS } from '@/utils/chartColors';
import type { NetWorthPeriodItem } from '@/api/types';

interface NetWorthChartDataItem extends NetWorthPeriodItem {
    name: string;
    isProjected?: boolean;
    /** Investable-assets value for this period (FI mode only) */
    investableValue?: number | null;
}

interface NetWorthChartTooltipProps {
    currency: string;
    /** Whether the chart is in Future mode (only shows Net Worth) */
    isFutureMode?: boolean;
    /** Whether FI mode is on (adds the investable-assets row) */
    showInvestable?: boolean;
    active?: boolean;
    payload?: Array<{
        payload: NetWorthChartDataItem;
    }>;
}

/**
 * Determine change color based on value type.
 * For debts: positive (more debt) is bad, negative (less debt) is good.
 * For net worth/assets: positive is good, negative is bad.
 */
function getChangeColor(change: number | null, isDebt = false): string {
    if (change === null) return '';
    if (isDebt) {
        return change > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';
    }
    return change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
}

/**
 * Format percentage change with sign.
 */
function formatPercentage(percent: number | null): string {
    if (percent === null) return '';
    const sign = percent >= 0 ? '+' : '';
    return `(${sign}${percent.toFixed(2)}%)`;
}

/**
 * Custom tooltip component for the Net Worth chart.
 * Displays all three values (Net Worth, Assets, Debts) for the hovered period.
 * Shows change from previous period for each value.
 * In Future mode, only displays Net Worth with a "Projected" label for projected items.
 */
export function NetWorthChartTooltip({
    currency,
    isFutureMode = false,
    showInvestable = false,
    active,
    payload,
}: NetWorthChartTooltipProps) {
    if (!active || !payload || payload.length === 0 || !payload[0]) {
        return null;
    }

    const data = payload[0].payload;
    const {
        periodLabel,
        netWorthValue,
        assetsValue,
        debtsValue,
        netWorthChange,
        netWorthChangePercent,
        assetsChange,
        assetsChangePercent,
        debtsChange,
        debtsChangePercent,
        isProjected,
        investableValue,
    } = data;

    const hasInvestable = showInvestable && investableValue != null;

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 sm:p-3 text-sm">
            {/* Period Label with Projected indicator */}
            <div className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm mb-1 sm:mb-3 flex items-center gap-2">
                {periodLabel}
                {isProjected && (
                    <span className="text-xs px-1.5 py-0.5 rounded-sm bg-accent/10 text-accent font-normal">
                        Projected
                    </span>
                )}
            </div>

            {/* Desktop: labeled horizontal layout with color dots */}
            <div className="hidden sm:block">
                {/* Net Worth */}
                <div className={`flex items-center justify-between gap-4 pb-2 ${!isFutureMode ? 'border-b border-gray-100 dark:border-gray-700 mb-2' : ''}`}>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS.netWorth.stroke }} />
                        <span className={`${isProjected ? 'text-gray-400 dark:text-gray-500 italic' : 'text-gray-500 dark:text-gray-400'}`}>
                            Net Worth:
                        </span>
                    </div>
                    <div className="text-right">
                        <MoneyDisplay
                            value={netWorthValue}
                            currency={currency}
                            size="sm"
                            className={`font-semibold ${isProjected ? 'text-gray-600 dark:text-gray-300 italic' : 'text-gray-900 dark:text-white'}`}
                        />
                        {netWorthChange !== null && !isProjected && (
                            <div className={`text-xs ${getChangeColor(netWorthChange)}`}>
                                {netWorthChange >= 0 ? '+' : ''}
                                <MoneyDisplay
                                    value={netWorthChange}
                                    currency={currency}
                                    size="sm"
                                    className="inline"
                                />
                                {' '}{formatPercentage(netWorthChangePercent)}
                            </div>
                        )}
                    </div>
                </div>

                {/* Investable assets - only in FI mode */}
                {hasInvestable && (
                    <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS.asset.stroke }} />
                            <span className="text-gray-500 dark:text-gray-400">Investable:</span>
                        </div>
                        <MoneyDisplay
                            value={investableValue}
                            currency={currency}
                            size="sm"
                            className="font-semibold text-gray-900 dark:text-white"
                        />
                    </div>
                )}

                {/* Assets - hidden in Future mode */}
                {!isFutureMode && (
                    <div className="flex items-center justify-between gap-4 pb-2 border-b border-gray-100 dark:border-gray-700 mb-2">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS.asset.stroke }} />
                            <span className="text-gray-500 dark:text-gray-400">Assets:</span>
                        </div>
                        <div className="text-right">
                            <MoneyDisplay
                                value={assetsValue}
                                currency={currency}
                                size="sm"
                                className="font-semibold text-gray-900 dark:text-white"
                            />
                            {assetsChange !== null && (
                                <div className={`text-xs ${getChangeColor(assetsChange)}`}>
                                    {assetsChange >= 0 ? '+' : ''}
                                    <MoneyDisplay
                                        value={assetsChange}
                                        currency={currency}
                                        size="sm"
                                        className="inline"
                                    />
                                    {' '}{formatPercentage(assetsChangePercent)}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Debts - hidden in Future mode */}
                {!isFutureMode && (
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS.debt.stroke }} />
                            <span className="text-gray-500 dark:text-gray-400">Debts:</span>
                        </div>
                        <div className="text-right">
                            <MoneyDisplay
                                value={debtsValue}
                                currency={currency}
                                size="sm"
                                className="font-semibold text-gray-900 dark:text-white"
                            />
                            {debtsChange !== null && (
                                <div className={`text-xs ${getChangeColor(debtsChange, true)}`}>
                                    {debtsChange >= 0 ? '+' : ''}
                                    <MoneyDisplay
                                        value={debtsChange}
                                        currency={currency}
                                        size="sm"
                                        className="inline"
                                    />
                                    {' '}{formatPercentage(debtsChangePercent)}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile: vertical stacked layout with labels */}
            <div className="sm:hidden space-y-1.5">
                {/* Net Worth */}
                <div>
                    <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS.netWorth.stroke }} />
                        <span className="text-xs text-gray-500 dark:text-gray-400">Net Worth</span>
                    </div>
                    <MoneyDisplay
                        value={netWorthValue}
                        currency={currency}
                        size="sm"
                        className={`font-semibold ${isProjected ? 'text-gray-600 dark:text-gray-300 italic' : 'text-gray-900 dark:text-white'}`}
                    />
                    {netWorthChange !== null && !isProjected && (
                        <div className={`text-xs ${getChangeColor(netWorthChange)}`}>
                            {formatPercentage(netWorthChangePercent)}
                        </div>
                    )}
                </div>

                {/* Investable assets - only in FI mode */}
                {hasInvestable && (
                    <div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS.asset.stroke }} />
                            <span className="text-xs text-gray-500 dark:text-gray-400">Investable</span>
                        </div>
                        <MoneyDisplay
                            value={investableValue}
                            currency={currency}
                            size="sm"
                            className="font-semibold text-gray-900 dark:text-white"
                        />
                    </div>
                )}

                {/* Assets - hidden in Future mode */}
                {!isFutureMode && (
                    <div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS.asset.stroke }} />
                            <span className="text-xs text-gray-500 dark:text-gray-400">Assets</span>
                        </div>
                        <MoneyDisplay
                            value={assetsValue}
                            currency={currency}
                            size="sm"
                            className="font-semibold text-gray-900 dark:text-white"
                        />
                        {assetsChange !== null && (
                            <div className={`text-xs ${getChangeColor(assetsChange)}`}>
                                {formatPercentage(assetsChangePercent)}
                            </div>
                        )}
                    </div>
                )}

                {/* Debts - hidden in Future mode */}
                {!isFutureMode && (
                    <div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS.debt.stroke }} />
                            <span className="text-xs text-gray-500 dark:text-gray-400">Debts</span>
                        </div>
                        <MoneyDisplay
                            value={debtsValue}
                            currency={currency}
                            size="sm"
                            className="font-semibold text-gray-900 dark:text-white"
                        />
                        {debtsChange !== null && (
                            <div className={`text-xs ${getChangeColor(debtsChange, true)}`}>
                                {formatPercentage(debtsChangePercent)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
